import React, { useCallback, useEffect, useState } from 'react';
import { Building2, MapPin } from 'lucide-react';
import { apiBase, authHeaders } from '../../utils/authHeaders';
import { useMobileAuth } from '../MobileAuthProvider';

const MobileScopeSwitcher = () => {
  const { token } = useMobileAuth();
  const [organizations, setOrganizations] = useState([]);
  const [locations, setLocations] = useState([]);
  const [organizationId, setOrganizationId] = useState(() => localStorage.getItem('current_organization_id') || '');
  const [locationId, setLocationId] = useState(() => localStorage.getItem('current_location_id') || '');
  const [error, setError] = useState('');

  const loadOrganizations = useCallback(async () => {
    if (!token) return;
    setError('');
    try {
      const response = await fetch(`${apiBase()}/api/platform/organizations`, {
        headers: authHeaders({ token }),
      });
      if (!response.ok) throw new Error(`Could not load organizations (${response.status})`);
      const data = await response.json();
      if (!Array.isArray(data)) throw new Error('Organization response was invalid');
      setOrganizations(data);
      localStorage.setItem('available_organizations', JSON.stringify(data));

      const stored = localStorage.getItem('current_organization_id') || '';
      const active = data.find((org) => org.id === stored);
      if (!active) {
        localStorage.removeItem('current_organization_id');
        localStorage.removeItem('current_location_id');
        setOrganizationId(data.length === 1 ? data[0].id : '');
        if (data.length === 1) localStorage.setItem('current_organization_id', data[0].id);
        setLocationId('');
      } else {
        setOrganizationId(active.id);
      }
    } catch (err) {
      setError(err.message || 'Could not load organizations');
    }
  }, [token]);

  useEffect(() => { loadOrganizations(); }, [loadOrganizations]);

  useEffect(() => {
    let cancelled = false;
    if (!token || !organizationId) {
      setLocations([]);
      return undefined;
    }

    (async () => {
      setError('');
      try {
        const response = await fetch(`${apiBase()}/api/locations`, {
          headers: authHeaders({ token, orgId: organizationId }),
        });
        if (!response.ok) throw new Error(`Could not load authorized sites (${response.status})`);
        const data = await response.json();
        if (!Array.isArray(data)) throw new Error('Site response was invalid');
        if (cancelled) return;
        setLocations(data);
        const stored = localStorage.getItem('current_location_id') || '';
        const active = data.find((site) => site.id === stored);
        if (active) {
          setLocationId(active.id);
        } else {
          localStorage.removeItem('current_location_id');
          const onlySite = data.length === 1 ? data[0] : null;
          setLocationId(onlySite?.id || '');
          if (onlySite) localStorage.setItem('current_location_id', onlySite.id);
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Could not load authorized sites');
      }
    })();

    return () => { cancelled = true; };
  }, [organizationId, token]);

  const selectOrganization = (nextId) => {
    if (!organizations.some((org) => org.id === nextId)) return;
    localStorage.setItem('current_organization_id', nextId);
    localStorage.removeItem('current_location_id');
    setOrganizationId(nextId);
    setLocationId('');
    window.location.reload();
  };

  const selectLocation = (nextId) => {
    if (!locations.some((site) => site.id === nextId)) return;
    localStorage.setItem('current_location_id', nextId);
    setLocationId(nextId);
    window.location.reload();
  };

  return (
    <section aria-label="Active organization and site" className="border-b border-surface-border bg-surface px-3 py-2">
      <div className="mx-auto flex w-full max-w-5xl flex-wrap items-center gap-2">
        <Building2 aria-hidden="true" className="h-4 w-4 text-ink-muted" />
        <label className="sr-only" htmlFor="mobile-active-organization">Organization</label>
        <select
          id="mobile-active-organization"
          aria-label="Organization"
          value={organizationId}
          onChange={(event) => selectOrganization(event.target.value)}
          className="h-9 min-w-0 flex-1 border border-surface-border bg-surface px-2 text-sm"
        >
          <option value="">Choose organization</option>
          {organizations.map((org) => <option key={org.id} value={org.id}>{org.name || org.id}</option>)}
        </select>
        <MapPin aria-hidden="true" className="h-4 w-4 text-ink-muted" />
        <label className="sr-only" htmlFor="mobile-active-site">Site</label>
        <select
          id="mobile-active-site"
          aria-label="Site"
          value={locationId}
          disabled={!organizationId || locations.length === 0}
          onChange={(event) => selectLocation(event.target.value)}
          className="h-9 min-w-0 flex-1 border border-surface-border bg-surface px-2 text-sm disabled:bg-surface-muted"
        >
          <option value="">{organizationId ? 'Choose site' : 'Select organization first'}</option>
          {locations.map((site) => <option key={site.id} value={site.id}>{site.name || site.id}</option>)}
        </select>
      </div>
      {error && <p role="alert" className="mx-auto mt-1 max-w-5xl text-xs text-danger">{error}</p>}
      {organizationId && locations.length === 0 && !error && (
        <p className="mx-auto mt-1 max-w-5xl text-xs text-amber-800">No authorized sites are available in this organization.</p>
      )}
    </section>
  );
};

export default MobileScopeSwitcher;
